import { DOMUtils } from '../../../../utils/dom'
import { IdentityEvent } from '../../../common/events/identity'
import { IProfile } from '../../../common/profile'
import { IAnalyticsProviderConfig } from '../adapter'

import { SetAttributes } from '../../../../domain/analytics/providers/mparticle/events/user/set-attributes'

import { UserEvents } from '../../../../domain/analytics/providers/mparticle/user-events'
import { logger } from '../../../../utils/common/logger'
import { GlobalStore } from '../../../../utils/storage/global'
import { Store } from '../../../../utils/storage/keys'
import { LocalStore } from '../../../../utils/storage/local'

export interface IWindow extends Window {
    mParticle: any
}

interface IMPCallbackResponse {
    getUser: () => any
}

declare let window: IWindow

const PPID_MINIMUM_CHARACTERS = 32
const userEvents = new UserEvents()

export class MParticle {

    /**
     * initialize mParticle
     * https://docs.mparticle.com/developers/sdk/web/getting-started/#add-the-sdk-snippet
     *
     * identifyRequest: IDSync request, contains desired indentify
     * request. mParticle will use recent/previous indentities if
     * not provided
     * https://docs.mparticle.com/developers/sdk/web/idsync/
     *
     * identityCallback: get executed on successfull completion of
     * IDSync. Provides mParticle init information
     */
    public initialize(
        creds: IAnalyticsProviderConfig,
        profile: IProfile): void {

        const identityRequest = this.buildUserIdentities(profile.uuid, profile.email)

        const config = {
            appVersion: '1.0.0',
            customFlags: {},
            identifyRequest: identityRequest,
            identityCallback: this.mParticleCallback.bind(this),
            isDebug: true,
            isDevelopmentMode: creds.isDev,
            isSandbox: true,
            sessionTimeout: 30,
            useCookieStorage: true,
            useNativeSdk: false,
        }

        // setup global object and queue
        window.mParticle = window.mParticle || { config: { rq: [] } }
        window.mParticle.config = { ...window.mParticle.config, ...config }
        window.mParticle.ready = (f: () => void) => window.mParticle.config.rq.push(f)

        // load JS
        DOMUtils.loadScript(`//jssdkcdns.mparticle.com/js/v2/${creds.account}/mparticle.js`)

    }

    /**
     * mParticle init callback
     */
    public async mParticleCallback(mPResponse: IMPCallbackResponse) {

        const currentIdentities: any = mPResponse.getUser().getUserIdentities().userIdentities

        // Check localStore for profile
        const localProfile: IProfile = LocalStore.get(Store.ID)
        if (localProfile && localProfile.uuid && currentIdentities.customerid !== localProfile.uuid) {
            const identityObject = this.buildUserIdentities(localProfile.uuid, localProfile.email, localProfile.id)
            window.mParticle.Identity.identify(identityObject, () => {
                SetAttributes(localProfile)
            })
        } else {
            let globalProfile: IProfile
            try {
                globalProfile = await GlobalStore.get(Store.ID)
            } catch (error) {
                logger('error', 'Unable to access global profile ', error)
            }
            if (globalProfile && globalProfile.id && globalProfile.id !== currentIdentities.other) {
                const identityObject = this.buildUserIdentities(globalProfile.uuid, globalProfile.email,
                    globalProfile.id)
                window.mParticle.Identity.identify(identityObject, () => {
                    SetAttributes(globalProfile)
                })
            } else {
                const identityChangeCallback = (data: any) => {

                    if(data.detail.uuid && currentIdentities.customerid !== data.detail.uuid &&
                        !data.detail.isLogin) {
                        const identityObject = this.buildUserIdentities(data.detail.uuid, data.detail.email,
                            data.detail.id)
                        window.mParticle.Identity.identify(identityObject, () => {
                            SetAttributes(data.detail)
                        })

                        // remove listener once identity updated - workaround for the multiple identity update calls
                        removeEventListener(IdentityEvent.IDENTITY_CHANGE, identityChangeCallback)

                    }
                }
                addEventListener(IdentityEvent.IDENTITY_CHANGE, identityChangeCallback)
            }
        }

        /**
         * Dispatcing mParticle Id as soon as it becomes available
         */
        if (window.mParticle.Identity.getCurrentUser()) {
            if (window.mParticle.Identity.getCurrentUser().getMPID()) {
                // Cheetah will also pull the PPID from localStorage so update accrodingly
                localStorage.setItem(Store.PPID,
                    this.buildPPIDfromMPID(window.mParticle.Identity.getCurrentUser().getMPID()))
                dispatchEvent(new CustomEvent(IdentityEvent.PROVIDER_ID_ASSIGNED, {
                    detail: { mpId: window.mParticle.Identity.getCurrentUser().getMPID() }
                }))
            }
        }

        /**
         * Dispatching mParticle Session UUID as soon as it becomes available
         */
        if (window.mParticle && window.mParticle.sessionManager.getSession()) {
            dispatchEvent(new CustomEvent(IdentityEvent.MP_READY, {
                detail: {
                    mpId: window.mParticle.Identity.getCurrentUser().getMPID(),
                    mpSessionUuid: window.mParticle.sessionManager.getSession()
                }
            }))
        }

        this.listenToUserEvents()
    }

    /***
     * mParticle ID:
     * All users are associated with a signed 64-bit integer called
     * the mParticle ID (MPID). On the web, this is surfaced as a string
     * due to the limitations of the Javascript number type.
     * Note that this value may be negative.
     * Many mParticle integrations can map the MPID as the external
     * user id for the given integration.
     * MPIDs are shared across browsers and devices depending on
     * your IDSync strategy and usage of the IDSync API.
     *
     * The PPID value must be:
     * Alphanumeric ([0-9 a-z A-Z]).
     * A minimum of 32 characters.
     * A maximum of 150 characters.
     * Hashed and meaningless to Google.
     * Only sent when the user can be identified by the
     * publisher—and not sent in cases when the user cannot be identified.
     * https://support.google.com/admanager/answer/2880055?hl=en
     */
    private buildPPIDfromMPID(mpid: string) {
        return ((mpid.startsWith('-'))
            ? 'ppidn' + mpid.replace('-', '')
            : 'ppidp' + mpid).padStart(PPID_MINIMUM_CHARACTERS, '0')
    }

    private listenToUserEvents() {
        document.addEventListener(IdentityEvent.NEW_REGISTRATION, async (event: any) => {
            let globalProfile: IProfile
            try {
                globalProfile = await GlobalStore.get(Store.ID)
            } catch (error) {
                logger('error', 'Unable to access global profile ', error)
            }
            const identityRequest = this.buildUserIdentities(event.detail.uuid, event.detail.email,
                globalProfile?globalProfile.id:undefined)
            SetAttributes(event.detail)
            userEvents.userLogin(identityRequest, true)
        })

        document.addEventListener(IdentityEvent.IDENTITY_LOGIN, (event: any) => {
            const identityRequest = this.buildUserIdentities(event.detail.uuid, event.detail.email)
            SetAttributes(event.detail)
            userEvents.userLogin(identityRequest, false)
        })
    }

    private buildUserIdentities(customerid?: string, email?: string, other?: string) {
        const userIdentities: any = {}
        if(customerid) {
            userIdentities.customerid = customerid
        }
        if(email) {
            userIdentities.email = email
        }
        if(other) {
            userIdentities.other = other
        }
        return {
            userIdentities
        }
    }
}
